import {Component, OnInit} from '@angular/core';
import {UserPrivilegeService} from '../../services/common/user-privilege/user-privilege.service';
import {RoutingGroupApiService} from '../../services/api/routing-group/routing-group-api.service';
import {AcDialogService} from 'ac-infra';
import * as _ from 'lodash';
import {RoutingGroupsDialogComponent} from './routing-groups-dialog/routing-groups-dialog.component';
import {RoutingRuleApiService} from '../../services/api/routing/routing-rule/routing-rule-api.service';
import {ConfirmationDialogComponent} from '../../dialogs/confirmation-dialog/confirmation-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {SnackBarService} from '../../services/common/snack-bar/snack-bar.service';
import {SortableListDialogComponent} from '../../dialogs/sortable-list-dialog/sortable-list-dialog.component';
import {RoutingGroupsSearchComponent} from './routing-groups-search/routing-groups-search.component';
import {SearchTermDefinitionInf} from '../../interfaces/common/search/search-term-definition-inf';
import Search from '../../utils/search';
import {RoutingRulesDialogComponent} from '../rules/routing-rules-dialog/routing-rules-dialog.component';
import {RoutingService} from '../services/routing.service';
import {MoveRuleDialogComponent} from '../rules/move-rule-dialog/move-rule-dialog.component';
import {LoaderService} from '../../services/common/loader-service/loader.service';
import {RoleTypeEnum} from '../../services/enums/role-type.enum';

const PREFIX_GROUP_NAME_PREFIX = '\u24bc ';

@Component({
  selector: 'app-groups',
  templateUrl: './groups.component.html',
  styleUrls: ['./groups.component.scss']
})
export class GroupsComponent implements OnInit {

  routingGroupsSearchComponent = RoutingGroupsSearchComponent;
  isUnauthorized: boolean;
  isInSearchProcess = false;

  selectedGroup: any;
  selectedRule: any;

  groups: any = [];
  rules = [];

  externalData;
  searchData;
  searchTerms: SearchTermDefinitionInf[];
  searchInput;
  numberOfGroups;

  constructor(public dialog: MatDialog,
              private snackBarService: SnackBarService,
              private dialogService: AcDialogService,
              private userPrivilege: UserPrivilegeService,
              private routingService: RoutingService,
              private routingRuleApiService: RoutingRuleApiService,
              private routingGroupApiService: RoutingGroupApiService, private loaderService: LoaderService) { }

  ngOnInit(): void {
    this.isUnauthorized = !this.userPrivilege.isAuthorized(RoleTypeEnum.ROUTING);

    this.initialize();
    this.setSearchOptions();
  }

  initialize(): void {
    this.loaderService.showMainContainer();
    this.routingGroupApiService.externalData().then(data => {
      this.externalData = _.cloneDeep(data);
      this.externalData.prefixGroups.forEach(pg => {
        pg.name = PREFIX_GROUP_NAME_PREFIX + pg.name;
      });
      this.prepareNetworkElements();
      this.getAllGroups(null, null);
    });
  }

  onGroupSelected(group): void {
    this.selectedRule = null;
    this.selectedGroup = group;
    this.getRulesForGroup(null);
  }

  deleteGroup(event?): void {
    const confirmationDialog = this.dialog.open(ConfirmationDialogComponent, {
      disableClose: true,
      width: '460px',
      data: {
        message: `Are you sure you want to delete the Routing Group: "${this.selectedGroup.name}" ?`
      }
    });

    confirmationDialog.componentInstance.onApply.subscribe(() => {
      this.routingGroupApiService.deleteGroup(this.selectedGroup.id).then(() => {
        confirmationDialog.close();
        this.snackBarService.display(`"${this.selectedGroup.name}" successfully deleted`);
        this.getAllGroups(this.getPreviousGroup(this.groups, this.selectedGroup), null);
      });
    });
  }

  addGroup(): void {
    this.routingGroupApiService.getExternalDataForAddOrEditGroup().then(data => {
      const groupsSlim = data[0];

      const dialogData: any = {
        data: {},
        timeConditionsOptions: data[1]
      };

      this.dialogService.open(RoutingGroupsDialogComponent, {
        dialogData,
        title: 'Add Group',
        onSubmit: () => {
          if (!_.isNil(this.selectedGroup)) {
            dialogData.data.selectedGroup = this.selectedGroup;
          }

          return this.routingGroupApiService.addGroup(this.createNewGroup(dialogData.data, groupsSlim)).then(group => {
            this.getAllGroups(this.selectedGroup, null);
          }).catch(() => {});
        },
      });
    });
  }

  editGroup(event?): void {
    this.routingGroupApiService.getExternalDataForAddOrEditGroup().then(data => {
      const groupsSlim = {
        timeConditions: data[0]
      };

      const dialogData = {
        data: _.cloneDeep(this.selectedGroup),
        timeConditionsOptions: data[1]
      };

      this.dialogService.open(RoutingGroupsDialogComponent, {
        dialogData,
        title: 'Edit Group',
        onSubmit: () => {
          dialogData.data.adminState = 'UNLOCKED';

          return this.routingGroupApiService.editGroup(dialogData.data).then(group => {
            this.getAllGroups(this.selectedGroup, null);
          }).catch(() => {});
        },
      });
    });
  }

  moveGroup(event?): void {
    const dialogData = {
      groups: this.groups,
      sentParameters: {prevId: -1, nextId: -1},
      selectedGroup: this.selectedGroup.id
    };

    this.dialogService.open(SortableListDialogComponent, {
      dialogData,
      title: 'Move Routing Group',
      onSubmit: () => {
        const formattedGroup = this.createGroupToMove(this.selectedGroup, dialogData.sentParameters);

        return this.routingGroupApiService.moveExistingGroup(this.selectedGroup.id, formattedGroup).then(group => {
          // this.sharedEventService.broadcast('closeMoveGroupDialog');
          this.getAllGroups(this.selectedGroup, this.selectedRule);
        }).catch(() => {});
      }
    });
  }

  onRuleSelected(rule): void {
    this.selectedRule = rule;
  }

  deleteRule(event?): void {
    const confirmationMessage = `Are you sure you want to delete the rule: "${this.selectedRule.name}" ?`;
    const confirmationDialog = this.dialog.open(ConfirmationDialogComponent, {
      disableClose: true,
      width: '460px',
      data: {
        message: confirmationMessage
      }
    });

    confirmationDialog.componentInstance.onApply.subscribe(() => {
      this.routingRuleApiService.deleteRules([this.selectedRule.id]).then((response) => {
        confirmationDialog.close();
        if (response && response.status === 200) {
          this.snackBarService.display(`"${this.selectedRule.name}" successfully deleted`);
          this.getAllGroups(this.selectedGroup ? this.selectedGroup : null,
            this.getPreviousRule(this.selectedGroup, this.selectedRule));
        }

      });
    });
  }

  addRule(): void {
    const newRule: any = {groupName: this.selectedGroup.name, rule: {}};

    const dialogData: any = {
      ruleData: newRule,
      externalData: this.externalData,
      isEditMode: false
    };

    this.dialogService.open(RoutingRulesDialogComponent, {
      width: 1300,
      height: 700,
      dialogData,
      title: 'Add Routing Rule',
      onSubmit: () => {
        dialogData.ruleToEdit.rrGroupId = this.selectedGroup.id;

        if (this.selectedRule !== null && this.selectedRule) {
          dialogData.ruleToEdit.selectedRuleId = this.selectedRule.id;
        }

        return this.routingGroupApiService.getGroupById(this.selectedGroup.id).then((group) => {
          const addRule = this.routingService.addPrevAndNextIdsToRule(dialogData.ruleToEdit, group);

          return this.routingRuleApiService.addNewRule(addRule).then((rule) => {
            this.getRulesForGroup(rule);
          });
        });
      }
    });
  }

  editRule(event?): void {
    this.routingRuleApiService.getRuleById(this.selectedRule.id).then(rule => {
      Object.assign(this.selectedRule, rule);

      const newRule = {groupName: this.selectedGroup.name, rule: this.selectedRule};

      const dialogData: any = {
        ruleData: newRule,
        externalData: this.externalData,
        isEditMode: false
      };

      this.dialogService.open(RoutingRulesDialogComponent, {
        width: 1300,
        height: 700,
        dialogData,
        title: 'Edit Routing Rule',
        onSubmit: () => {
          dialogData.ruleToEdit.id = this.selectedRule.id;
          dialogData.ruleToEdit.rrGroupId = this.selectedRule.rrGroupId;

          return this.routingRuleApiService.editExistingRule(dialogData.ruleToEdit).then(value => {
            this.updateRuleInRulesList(value);
          });
        }
      });
    });
  }

  moveRule(rule): void {
    const moveRuleObject = this.routingService.buildMoveRuleObject(rule);

    const rules = this.groups.flatMap(group => group.routingRules);

    const ruleId = this.isRuleObject(moveRuleObject.rule) ? moveRuleObject.rule.id : moveRuleObject.rule;
    const sentParameters = {nextId: -1, prevId: -1, originalRuleId: ruleId, groupId: null};

    const dialogData = {
      groups: this.groups,
      rules: this.routingService.populateNames(rules),
      sentParameters,
      selectedGroup: this.selectedGroup.id
    };

    this.dialogService.open(MoveRuleDialogComponent, {
      dialogData,
      title: 'Move Routing Rule',
      onSubmit: () => {
        this.routingRuleApiService.getRuleById(ruleId).then( r => {
          const returnedRule = this.routingService.createRuleToMove(r, sentParameters);

          this.routingRuleApiService.moveExistingRule(ruleId, returnedRule).then(data => {
            this.getAllGroups(this.selectedGroup, this.selectedRule);
          }).catch(() => {});
        });
      }
    });

  }

  onSearch(searchObj): void {
    this.isInSearchProcess = true;
    this.searchInput = !_.isEmpty(searchObj.searchString) ? searchObj.searchString : searchObj.filter;
    this.getAllGroups(null, null);
  }

  duplicateRule(): void {
    this.routingRuleApiService.getRuleById(this.selectedRule.id).then(rule => {
      const newRule = {groupName: this.selectedGroup.name, rule};

      const dialogData: any = {
        ruleData: newRule,
        externalData: this.externalData,
        isEditMode: false
      };

      this.dialogService.open(RoutingRulesDialogComponent, {
        width: 1300,
        height: 700,
        dialogData,
        title: 'Add Routing Rule',
        onSubmit: () => {
          dialogData.ruleToEdit.rrGroupId = this.selectedGroup.id;

          if (this.selectedRule !== null && this.selectedRule) {
            dialogData.ruleToEdit.selectedRuleId = this.selectedRule.id;
          }

          return this.routingGroupApiService.getGroupById(this.selectedGroup.id).then((group) => {
            const addRule = this.routingService.addPrevAndNextIdsToRule(dialogData.ruleToEdit, group);

            return this.routingRuleApiService.addNewRule(addRule).then((updateRule) => {
              this.getRulesForGroup(updateRule);
            });
          });
        }
      });
    });
  }

  refresh(): void {
    this.getAllGroups(null, null);
  }

  private prepareNetworkElements(): void {
    let index = 1;

    this.externalData.voipPeers.forEach(voipPeer => {
      voipPeer.elementType = 'Online VoIP Peer';
      index++;
    });

    this.externalData.nodes.forEach(node => {
      node.elementType = 'Online Node';
      index++;
    });

    this.externalData.pCons.forEach(pcon => {
      pcon.elementType = 'Online Peer Connection';
      pcon.name = pcon.name + ' (' + pcon.nodeName + ')';
      index++;
    });

    this.externalData.offlineNodes.forEach(offline => {
      offline.elementType = 'Offline Node';
      index++;
    });

    this.externalData.offlineVoipPeers.forEach(offline => {
      offline.elementType = 'Offline VoIP Peer';
      index++;
    });

    this.externalData.offlinePCons.forEach(offline => {
      offline.elementType = 'Offline Peer Connection';
      offline.name = offline.name + ' (' + offline.nodeName + ')';
      index++;
    });
  }

  private getAllGroups(selectedGroup, selectedRule): void {
    const parameters = !_.isEmpty(this.searchInput) ? {filter: this.searchInput} : {};

    this.loaderService.showMainContainer();
    this.routingGroupApiService.getGroups(parameters).then(groups => {
      this.groups = groups;
      this.numberOfGroups = groups.length;

      if (this.groups.length > 0) {
        this.selectGroup(selectedGroup);
        this.getRulesForGroup(selectedRule);
      } else {
        this.clearData();
      }
      this.loaderService.hideMainContainer();
    }).catch(() => {
      this.loaderService.hideMainContainer();
    });
  }

  private isRuleObject(rule): any {
    return rule && !_.isNil(rule.id);
  }

  private selectGroup(group): void {
    this.selectedGroup = group ? this.groups.find(g => g.id === group.id) : this.groups[0];
  }

  private getRulesForGroup(selectRuleId): void {
    const parameters = {groupId: this.selectedGroup.id, filter: this.searchInput};
    this.loaderService.showMainContainer();
    this.routingGroupApiService.getRulesForGroup(parameters).then(rules => {
      rules.routingRules.forEach(rule => {
        rule.adminStateBoolean = _.isEqual(rule.adminState, 'LOCKED');
        rule.testModeBoolean = _.isEqual(rule.testMode, 'LOCKED');
      });

      this.rules = rules.routingRules;
      this.selectedGroup.routingRules = rules.routingRules;

      if (this.rules.length > 0) {
        this.selectRule(selectRuleId);
      }
      this.loaderService.hideMainContainer();
    }).catch(() => {
      this.loaderService.hideMainContainer();
    });
  }

  private openGroupSettingsDialogForEdit(groups, group, externalData): void {
    const okClickEvent = () => {
      group.adminState = 'UNLOCKED';

      this.routingGroupApiService.editGroup(group).then( () => {
        this.getAllGroups(this.selectedGroup, this.selectedRule);
        // this.sharedEventService.broadcast('closeGroupSettingsDialog');
      }).catch(() => {});
    };

    // this.sharedEventService.broadcast('openGroupSettingsDialog', groups, group, true, externalData, okClickEvent);
  }

  private clearData(): void {
    this.rules = [];
    this.groups = [];
    this.selectedGroup = undefined;
    this.selectedRule = undefined;
  }

  private selectRule(rule): void {
    this.selectedRule = rule ? this.rules.find(r => r.id === rule.id) : this.rules[0];
  }

  private createGroupToMove(group, parameters): any {
    return {
      name: group.name,
      adminState: 'UNLOCKED',
      prevRRGroupId: parameters.prevId,
      nextRRGroupId: parameters.nextId,
      tbrConditionsIds: group.tbrConditionsIds
    };
  }

  private createNewGroup(oldGroup, groups): any {
    const prevId = this.calculatePrevGroupId(oldGroup.selectedGroup, 'After', groups);
    const nextId = this.calculateNextGroupId(oldGroup.selectedGroup, 'After', groups);

    return {
      name: oldGroup.name,
      adminState: 'UNLOCKED',
      prevRRGroupId: prevId,
      nextRRGroupId: nextId,
      tbrConditionsIds: oldGroup.tbrConditionsIds
    };
  }

  private calculatePrevGroupId(group, position, groups): any {
    if (!group) {
      return !_.isEmpty(groups) ? groups[groups.length - 1].id : -1;
    } else {
      return _.isEqual(position, 'Before') ? this.calculateIdHelperGroups(group, groups, -1) : group.id;
    }
  }

  private calculateNextGroupId(group, position, groups): any {
    if (!group) {
      return -1;
    } else {
      return _.isEqual(position, 'After') ? this.calculateIdHelperGroups(group, groups, 1) : group.id;
    }
  }

  private calculateIdHelperGroups(group, groups, operator): any {
    const rulesCount = groups.length;

    for (let i = 0; i < rulesCount; i++) {
      if (groups[i].id === group.id) {
        return !!(groups[i + operator]) ? groups[i + operator].id : -1;
      }
    }

    return -1;
  }

  private getPreviousGroup(groups: any, selectedGroup: any): any {
    return this.getPreviousItem(groups, selectedGroup);
  }

  private getPreviousItem(items, selectedItem): any {
    const selectedGroupIndex = this.getItemIndex(items, selectedItem);

    if (items.length === 0) {
      this.selectedRule = null;
      return null;
    } else if (selectedGroupIndex === 0) {
      return (items.length > 1) ? items[1] : null;
    }

    return (selectedGroupIndex > 0) ? items[selectedGroupIndex - 1] : items[0];
  }

  private getItemIndex(items, selectedItem): any {
    return items.findIndex(item => item.id === selectedItem.id);
  }

  private setSearchOptions(): void {
    const discardOptions = [{text: 'True', value: 'True'}, {text: 'False', value: 'False'}];
    const registeredUsersOptions = [{text: 'True', value: 'True'}, {text: 'False', value: 'False'}];
    const isContinueWithNodeTablesOptions = [{text: 'True', value: 'True'}, {text: 'False', value: 'False'}];
    const adminStateOptions = [{text: 'True', value: 'UNLOCKED'}, {text: 'False', value: 'LOCKED'}];
    const qualityOptions = [{text: 'Good', value: 'GOOD'}, {text: 'Fair', value: 'FAIR'}];
    const requestTypeOptions = [{text: 'Call', value: 'CALL'}, {text: 'Register', value: 'REGISTER'}];

    this.searchData = {
      discardOptions,
      registeredUsersOptions,
      isContinueWithNodeTablesOptions,
      adminStateOptions,
      qualityOptions,
      requestTypeOptions
    };

    this.searchTerms = [
      {term: 'adminState', operator: '=', displayName: 'Live', displayValue: Search.getOptionValue(adminStateOptions)},
      {term: 'testMode', operator: '=', displayName: 'Test', displayValue: Search.getOptionValue(adminStateOptions)},
      {term: 'quality', operator: '=', displayName: 'Quality', displayValue: Search.getOptionValue(qualityOptions)},
      {term: 'isRegisteredUsers', operator: '=', displayName: 'Registered Users',
        displayValue: Search.getOptionValue(registeredUsersOptions)},
      {term: 'isDiscard', operator: '=', displayName: 'Discard', displayValue: Search.getOptionValue(discardOptions)},
      {term: 'isLoopback', operator: '=', displayName: 'Loopback', displayValue: Search.getOptionValue(discardOptions)},
      {term: 'isContinueWithNodeInternalTables', operator: '=',
        displayName: 'Stop ARM Routing', displayValue: Search.getOptionValue(isContinueWithNodeTablesOptions)},
      {term: 'requestType', operator: '=', displayName: 'Call Type', displayValue: Search.getOptionValue(requestTypeOptions)},
      {term: 'rrGroupName', operator: ':', displayName: 'RR Group Name'},
      {term: 'rrName', operator: ':', displayName: 'RR Name'}
    ];
  }

  private getPreviousRule(selectedGroup: any, selectedRule: any): any {
    const currentGroupRoutingRules = selectedGroup && selectedGroup.routingRules ? selectedGroup.routingRules : [];

    return this.getPreviousItem(currentGroupRoutingRules, selectedRule);
  }

  private updateRuleInRulesList(updatedRule): void {
    const index = this.rules.findIndex(r => r.id === updatedRule.id);

    this.addAdditionalInformationToUpdatedRule(updatedRule);

    this.rules[index] = updatedRule;
    this.rules = [...this.rules];
  }

  private addAdditionalInformationToUpdatedRule(updatedRule): void {
    updatedRule.open = true;
    updatedRule.adminStateBoolean = _.isEqual(updatedRule.adminState, 'LOCKED');
    updatedRule.testModeBoolean = _.isEqual(updatedRule.testMode, 'LOCKED');
  }

  isNoGroupSelection(): boolean {
    return _.isNil(this.selectedGroup);
  }

  isNoRuleSelection(): boolean {
    return _.isNil(this.selectedRule);
  }
}
